/**
 * Utility function for doing CSS math in JavaScript
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
export const CSS_LENGTH_UNITS = ['cm', 'mm', 'in', 'px', 'pt', 'pc', 'em', 'ex', 'ch', 'rem', 'vw', 'vh', 'vmin', 'vmax', '%']

export const CSS_LENGTH_REGEX = /([+-]?[0-9]+(.?[0-9]+)?)($|cm|mm|in|px|pt|pc|em|ex|ch|rem|vw|vh|vmin|vmax|%)/

/**
 * Parses CSS length value into number and unit
 * @param {String|Number} str The CSS length value
 * @return {Array} A pair of number (in original data type) and unit
 */
export const parseCSSLength = (str) => {
  if (typeof str === 'number') {
    return [str, undefined]
  }

  str = str.trim()
  const results = str.match(CSS_LENGTH_REGEX)

  // Returns capture group 1 (number) and 3 (unit)
  return [results[1], results[3]]
}

const operate = {
  '+': (a, b) => a + b,
  '-': (a, b) => a - b,
  '*': (a, b) => a * b,
  '/': (a, b) => a / b
}

/**
 * Do arithmetic using CSS length values
 * @param {String} operator The math operator (+, -, *, /)
 * @param {...String | ...Number} operands The operands
 */
export const calc = (operator, ...operands) => {
  // Find first unit in operands and use it. If not found, return unitless number in String
  let finalUnit
  const [num, unit] = parseCSSLength(operands.shift())
  finalUnit = finalUnit || unit

  const result = operands.reduce((acc, value) => {
    const [num, unit] = parseCSSLength(value)
    finalUnit = finalUnit || unit

    return operate[operator](acc, parseFloat(num))
  }, parseFloat(num))

  return `${result.toFixed(2)}${finalUnit || ''}`
}

/**
 * Add CSS length values (e.g. 10px + 2px)
 * @param {...String | ...Number} operands Numbers to add
 */
export const add = (...operands) => calc('+', ...operands)

/**
 * Subtract CSS length values (e.g. 42px * 2px)
 * @param {...String | ...Number} operands Numbers to subtract
 */
export const subtract = (...operands) => calc('-', ...operands)

/**
 * Multiply CSS length values (e.g. 10px * 5)
 * @param {...String | ...Number} operands Numbers to multiply
 */
export const multiply = (...operands) => calc('*', ...operands)

/**
 * Divide CSS length values (e.g. 42px / 2)
 * @param {...String | ...Number} operands Numbers to divide
 */
export const divide = (...operands) => calc('/', ...operands)
